import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { X, MapPin, Clock, MessageCircle, Flame, Wine, Navigation, BookOpen, Flag, Search } from "lucide-react";
import { toast } from "sonner";
import { fullUrl } from "@/components/app/ImageUploader";
import { DAYS, brl, categoryLabel, hoursLabel, todayIndex, waLink, mapsLink, fmtDate, fmtDateTime } from "@/lib/adega";
import { reportTarget } from "@/lib/report";

const track = (id, kind) => api.post(`/adegas/${id}/click`, { kind }).catch(() => {});

function ProductLine({ p }) {
  return (
    <li className={`flex items-center gap-3 py-2.5 ${p.out_of_stock ? "opacity-40" : ""}`} aria-disabled={p.out_of_stock} data-testid={`public-product-${p.product_id}`}>
      {p.photo_url && <img src={fullUrl(p.photo_url)} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${p.out_of_stock ? "line-through" : ""}`}>{p.name}</div>
        <div className="text-[11px] text-muted-foreground">{p.description ? `${p.description} · ` : ""}Preço atualizado em {fmtDate(p.price_updated_at)}</div>
        {p.promo && <div className="text-[11px] text-primary">Promoção até {fmtDateTime(p.promo_until)}</div>}
      </div>
      <div className="text-sm whitespace-nowrap text-right">
        {p.out_of_stock ? <Badge variant="outline" className="border-white/20 text-[10px]">Em falta</Badge>
          : p.promo ? <><s className="text-muted-foreground text-xs block">{brl(p.price)}</s><span className="text-primary font-bold">{brl(p.promo_price)}</span></> : brl(p.price)}
      </div>
      <button onClick={() => reportTarget("product", p.product_id, "este produto")} className="h-8 w-6 grid place-items-center text-muted-foreground/60 press" aria-label="Denunciar produto" data-testid={`report-product-${p.product_id}`}><Flag className="w-3 h-3" /></button>
    </li>
  );
}

function Catalog({ products }) {
  const [q, setQ] = useState("");
  const list = products.filter((p) => !q || `${p.name} ${p.description || ""}`.toLowerCase().includes(q.toLowerCase()));
  const promos = list.filter((p) => p.promo && !p.out_of_stock);
  const groups = list.filter((p) => !promos.includes(p)).reduce((acc, p) => { const c = categoryLabel(p.category); (acc[c] = acc[c] || []).push(p); return acc; }, {});
  return (
    <div className="space-y-4" data-testid="adega-catalog">
      <div className="rounded-xl bg-primary/10 border border-primary/40 p-3 text-[11px] leading-relaxed" data-testid="catalog-warning">
        Preços de retirada, definidos pela adega. Venda e entrega são combinadas diretamente com a adega. <strong>Proibido para menores de 18 anos.</strong> Beba com moderação.
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto" className="pl-9" data-testid="catalog-search-input" />
      </div>
      {products.length === 0 && <p className="text-xs text-muted-foreground">Catálogo ainda não publicado.</p>}
      {products.length > 0 && list.length === 0 && <p className="text-xs text-muted-foreground">Nenhum produto encontrado.</p>}
      {promos.length > 0 && (
        <div className="rounded-2xl border border-primary/60 bg-primary/10 p-3" data-testid="promo-of-day">
          <div className="flex items-center gap-1.5 text-sm font-bold text-primary mb-1"><Flame className="w-4 h-4" /> Promoção do dia</div>
          <ul className="divide-y divide-white/5">{promos.map((p) => <ProductLine key={p.product_id} p={p} />)}</ul>
        </div>
      )}
      {Object.entries(groups).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{cat}</div>
          <ul className="divide-y divide-white/5">{items.map((p) => <ProductLine key={p.product_id} p={p} />)}</ul>
        </div>
      ))}
    </div>
  );
}

function ActionButton({ href, onClick, icon: Icon, label, className, testid }) {
  const cls = `h-12 rounded-xl font-semibold flex items-center justify-center gap-2 text-sm press ${className}`;
  return href
    ? <a href={href} target="_blank" rel="noreferrer" onClick={onClick} className={cls} data-testid={testid}><Icon className="w-4 h-4" /> {label}</a>
    : <button onClick={onClick} className={cls} data-testid={testid}><Icon className="w-4 h-4" /> {label}</button>;
}

export default function AdegaSheet({ adegaId, onClose }) {
  const [a, setA] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);
  useEffect(() => {
    api.get(`/adegas/${adegaId}`).then(({ data }) => setA(data)).catch((e) => { toast.error(formatApiError(e)); onClose?.(); });
  }, [adegaId]); // eslint-disable-line

  const today = todayIndex();
  const openCatalog = () => { if (!showCatalog) track(adegaId, "catalog"); setShowCatalog((v) => !v); };
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-3xl bg-card border-t border-white/10 p-0" data-testid="adega-sheet">
        <div className="sticky top-0 z-10 flex justify-end px-4 py-2 bg-card/95 backdrop-blur">
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-muted grid place-items-center press" data-testid="close-adega-sheet"><X className="w-4 h-4" /></button>
        </div>
        {!a ? <div className="p-8 text-center text-muted-foreground text-sm">Carregando...</div> : (
          <div className="px-5 pb-28">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full overflow-hidden border-[3px] border-primary bg-muted grid place-items-center shrink-0">
                {a.photo_url ? <img src={fullUrl(a.photo_url)} alt="" className="w-full h-full object-cover" /> : <Wine className="w-8 h-8 text-primary" />}
              </div>
              <div className="min-w-0">
                <Badge variant="outline" className="border-primary/50 text-primary text-[10px] mb-1">Adega</Badge>
                <SheetTitle className="text-2xl heading font-extrabold leading-tight" data-testid="adega-sheet-name">{a.name}</SheetTitle>
                <Badge className={a.is_open ? "bg-emerald-500 text-black mt-1" : "bg-muted text-muted-foreground mt-1"} data-testid="adega-sheet-status">{a.is_open ? "Aberta agora" : "Fechada"}</Badge>
              </div>
            </div>
            {a.description && <p className="mt-4 text-sm text-foreground/90">{a.description}</p>}
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-primary shrink-0" /> <span data-testid="adega-sheet-address">{a.address}</span></div>
              <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-primary shrink-0" /> Hoje: {hoursLabel(a.hours[String(today)])}</div>
            </div>
            <details className="mt-2 text-xs text-muted-foreground" data-testid="adega-week-hours">
              <summary className="cursor-pointer py-1">Ver horários da semana</summary>
              <ul className="mt-1 space-y-0.5">
                {DAYS.map((d, i) => <li key={d} className={`flex justify-between ${i === today ? "text-foreground font-semibold" : ""}`}><span>{d}</span><span>{hoursLabel(a.hours[String(i)])}</span></li>)}
              </ul>
            </details>
            <div className="mt-4 grid grid-cols-1 gap-2">
              <ActionButton onClick={openCatalog} icon={BookOpen} label={showCatalog ? "Esconder catálogo" : "Ver catálogo"} className="bg-primary text-primary-foreground" testid="adega-catalog-button" />
              <div className="grid grid-cols-2 gap-2">
                <ActionButton href={waLink(a.whatsapp, `Oi! Vi a ${a.name} no Enkosta.`)} onClick={() => track(adegaId, "whatsapp")} icon={MessageCircle} label="Chamar no WhatsApp" className="bg-emerald-500 text-black" testid="adega-whatsapp-button" />
                <ActionButton href={mapsLink(a.lat, a.lng)} onClick={() => track(adegaId, "directions")} icon={Navigation} label="Como chegar" className="bg-muted" testid="adega-directions-button" />
              </div>
            </div>
            {showCatalog && <div className="mt-6"><Catalog products={a.products} /></div>}
            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground leading-relaxed">O Enkosta só divulga a adega e não vende bebidas. Se beber, não dirija.</p>
              <button onClick={() => reportTarget("adega", a.id, "esta adega")} className="text-xs text-muted-foreground flex items-center gap-1 press shrink-0" data-testid="report-adega-button"><Flag className="w-3.5 h-3.5" /> Denunciar</button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
